// QA-136 (Kelly) — mobile-web View-Wheel config + floating-control visibility.
//
// Two Kelly-reported bugs on the mobile View-Wheel are encoded here as pure,
// testable rules so the JSX stays declarative:
//
// 1) A floating label-toggle control (Styled.ToggleLabelIconContainer, an
//    absolutely-positioned FAB) kept rendering OVER an opened segment panel,
//    obscuring the panel's caption text. It was hidden while the segment LIST
//    was open but NOT while a single segment panel was open. shouldShowLabelToggle
//    now hides it whenever a segment panel is open (selectedArc set) as well.
//
// 2) Segment name pills were clipped to "..." by default because the mobile
//    surface asked createPie for a segmentLableLimit of 10 chars, while the
//    hover/reveal path shows up to 25. MOBILE_VIEW_SEGMENT_LABEL_LIMIT raises
//    the default to match the reveal (25) so names are readable at a glance.

// Readable default character limit for the mobile View-Wheel rim-pill labels.
// Kept in agreement with createPie's highlight/reveal path (25 chars) so the
// default pill shows the same text the reveal would.
export const MOBILE_VIEW_SEGMENT_LABEL_LIMIT = 25;

/**
 * Whether the floating "toggle labels" control should be shown.
 * Hidden while either the segment LIST or a single segment PANEL is open, so it
 * can never overlap opened-segment content.
 * @param {{ openSegmentList?: boolean, selectedArc?: any }} state
 * @returns {boolean}
 */
export function shouldShowLabelToggle({ openSegmentList, selectedArc } = {}) {
  return !isSegmentSurfaceOpen({ openSegmentList, selectedArc });
}

/**
 * T22.225 (UX-25, MEASURED web-stg main.dd06f65e, /wheel/508170, 390x844):
 * with a single segment open (selectedArc) the fixed "Get the app" pill and
 * the Help FAB sat over the segment's text and images (111/111 sample points
 * under the pill were segment content). The Help FAB's hide signal
 * (setSegmentPanelOpen) was keyed on the segment LIST only, and the pill had
 * no gate at all. Same rule as the label toggle above: any open segment
 * surface (the list or a single segment) hides the floating controls.
 * @param {{ openSegmentList?: boolean, selectedArc?: any }} state
 * @returns {boolean}
 */
export function isSegmentSurfaceOpen({ openSegmentList, selectedArc } = {}) {
  return !!openSegmentList || !!selectedArc;
}

// T22.123 (audit-10120531, MEASURED web-stg main.128d9358.js, /wheel/507968,
// 12 segments, 390x844, Chromium + WebKit): the Segment 12 rim pill sat under
// the icon toolbar (6/9 sample points covered) and the Toggle Labels icon sat
// on Segment 3 (3/9). Two causes on this page:
//   1) WheelView only reserved a fixed 5vh above the wheel box, but the
//      renderer-v2 engine pushes a crowded noon pill out by whole radius
//      tiers (assignRimPillRadiusTiers / phaseNoonPillOutward, geometry.js),
//      so on 507968 the Segment 12 pill rose 65px above the wheel box —
//      past the top of ContentView, under the z-index:100 Header.
//   2) the toggle icon was pinned at top:12svh, i.e. inside the right-hand
//      2-3 o'clock pill band.
// The icon now sits at the top of ContentView (LABEL_TOGGLE_TOP_PX), and
// useRimPillClearance pads WheelView just enough that no MEASURED pill sits
// above ContentView's top or under the icon. A keep-out only pushes the pills
// that share its columns, so a lane-wide reserve never shoves the bottom
// pills down under the fixed "Get the app" pill.

// Top offset (px) of the label-toggle icon inside ContentView.
export const LABEL_TOGGLE_TOP_PX = 8;

// Gap (px) kept between a rim pill and a keep-out edge above it.
export const RIM_PILL_TOP_GAP_PX = 6;

/**
 * Top padding (px) WheelView needs so no rim pill sits above the bottom edge
 * of a keep-out box it shares columns with (ContentView's top edge spans the
 * whole width; the toggle icon only its own columns).
 * Shift-invariant: `pills` are measured with `currentPadding` already
 * applied, so the result does not feed back on itself.
 * @param {{
 *   pills: Array<{top:number,left:number,right:number}>,
 *   currentPadding?: number,
 *   keepOuts?: Array<{bottom:number,left?:number,right?:number}>,
 *   gap?: number,
 * }} p  keepOut left/right omitted = full width
 * @returns {number} whole px, 0 when no pill is measured or none is covered
 */
export function rimPillClearancePadding({
  pills = [],
  currentPadding = 0,
  keepOuts = [],
  gap = RIM_PILL_TOP_GAP_PX,
} = {}) {
  let need = 0;
  for (const pill of pills) {
    if (!pill || !Number.isFinite(pill.top)) continue;
    const unpaddedTop = pill.top - (currentPadding || 0);
    for (const k of keepOuts) {
      if (!k || !Number.isFinite(k.bottom)) continue;
      const sharesColumns =
        !(Number.isFinite(k.left) && Number.isFinite(k.right)) ||
        (pill.left < k.right && pill.right > k.left);
      if (sharesColumns) need = Math.max(need, k.bottom + gap - unpaddedTop);
    }
  }
  return Math.max(0, Math.ceil(need));
}
